/**
 * /prompt-grader: sends the prompt to POST /api/check (Jev, TypeSafe's classifier) and shows which
 * of four parts the task needs that the prompt leaves out. "Fix it for me" is the shared anonymous
 * enhance flow in landing.js (same #hero* ids), which owns the free-try budget.
 */

import { BACKEND_URL } from '../utils/config.js';
import { getAttribution } from '../utils/attribution.js';

const MIN_CHARS = 15;
const MAX_CHARS = 4000;
const NEEDS_THRESHOLD = 0.6;
const REQUEST_TIMEOUT_MS = 20000;
const USE_MOCK = new URLSearchParams(window.location.search).get('mock') === 'true';

const ITEMS = {
    audience: {
        label: 'Who it\'s for',
        tip: 'Say who will read it, for example "for busy parents on our waitlist".'
    },
    format: {
        label: 'Format and length',
        tip: 'Say the shape and size, for example "3 bullet points, under 100 words".'
    },
    context: {
        label: 'Your details',
        tip: 'Paste or describe the facts only you know: the product, the data, the code.'
    },
    success: {
        label: 'What a good result looks like',
        tip: 'Say what the answer must achieve, for example "so readers book a demo".'
    }
};

const TASK_TYPES = {
    code: 'Code',
    data_transform: 'Cleaning or converting data',
    writing_for_people: 'Writing for other people',
    analysis: 'Analysis or comparison',
    factual_question: 'A question or explanation',
    creative: 'Creative writing',
    follow_up: 'A follow-up in an existing chat'
};

const STATE_LABELS = {
    missing: 'Missing',
    borderline: 'Could add',
    covered: 'Covered',
    skipped: 'Not needed for this task'
};

const $ = (id) => document.getElementById(id);

let requestId = 0;
let lastFixedChecked = '';

function capture(event, props) {
    if (typeof window.posthog !== 'undefined') {
        window.posthog.capture(event, { ...getAttribution(), ...props, page: 'prompt-grader' });
    }
}

class CheckError extends Error {}

async function checkPrompt(prompt) {
    if (USE_MOCK) {
        const { mockCheckPrompt } = await import('../utils/api-mock.js');
        return mockCheckPrompt(prompt);
    }

    let response;
    try {
        response = await fetch(`${BACKEND_URL}/api/check`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prompt }),
            signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS)
        });
    } catch {
        throw new CheckError('Could not reach the prompt check. Check your connection and try again.');
    }

    let body = null;
    try {
        body = await response.json();
    } catch { /* non-JSON error page */ }

    if (!response.ok) {
        throw new CheckError(body?.message || 'The prompt check is unavailable right now. Please try again in a minute.');
    }
    if (!body?.items) {
        throw new CheckError('The prompt check sent back something unexpected. Please try again.');
    }
    return body;
}

function itemState(item, followUp) {
    if (followUp) return 'skipped';
    if (item.status === 'missing') return 'missing';
    if (item.status === 'borderline') return 'borderline';
    return item.needs !== null && item.needs >= NEEDS_THRESHOLD ? 'covered' : 'skipped';
}

function plural(count, word) {
    return `${count} ${word}${count === 1 ? '' : 's'}`;
}

function summaryText(result) {
    if (result.followUp) return 'This reads like a follow-up in an existing chat, so nothing is flagged.';
    const missing = result.missing.length;
    const could = result.borderline.length;
    if (!missing && !could) return 'Nothing missing';
    const parts = [];
    if (missing) parts.push(`${plural(missing, 'thing')} missing`);
    if (could) parts.push(`${could} you could add`);
    return parts.join(', ');
}

function renderItems(result) {
    for (const key of Object.keys(ITEMS)) {
        const row = document.querySelector(`[data-grade-item="${key}"]`);
        if (!row) continue;
        const item = result.items[key] || { needs: null, status: 'ok' };
        const state = itemState(item, result.followUp);
        row.dataset.state = state;
        row.querySelector('[data-grade-state]').textContent = STATE_LABELS[state];
        const tip = row.querySelector('[data-grade-tip]');
        tip.textContent = ITEMS[key].tip;
        tip.hidden = state !== 'missing' && state !== 'borderline';
    }
}

function renderResult(result) {
    $('gradeTask').textContent = TASK_TYPES[result.taskType] || 'Not sure';
    const summary = $('gradeSummary');
    summary.textContent = summaryText(result);
    summary.dataset.state = result.missing.length ? 'missing' : result.borderline.length ? 'borderline' : 'covered';
    renderItems(result);
}

function setPending(pending) {
    $('gradeBtn').disabled = pending || !$('heroPromptInput').value.trim();
    $('gradePending').hidden = !pending;
    if (pending) {
        $('gradeEmpty').hidden = true;
        $('gradeResult').hidden = true;
        $('fixedScore').hidden = true;
        showStatus('');
    }
}

function showStatus(message) {
    const status = $('gradeStatus');
    status.textContent = message;
    status.hidden = !message;
}

async function grade() {
    const input = $('heroPromptInput');
    const text = input.value.trim();
    if (!text) {
        input.focus();
        return;
    }
    if (text.length < MIN_CHARS) {
        showStatus(`Prompts must be between ${MIN_CHARS} and ${MAX_CHARS} characters.`);
        return;
    }

    const mine = ++requestId;
    setPending(true);
    try {
        const result = await checkPrompt(text);
        if (mine !== requestId) return;
        renderResult(result);
        $('gradeResult').hidden = false;
        $('fixBlock').hidden = false;
        lastFixedChecked = '';
        capture('prompt_grader_graded', {
            task_type: result.taskType,
            missing_count: result.missing.length,
            borderline_count: result.borderline.length,
            prompt_length: text.length
        });
        if (window.lucide) window.lucide.createIcons();
    } catch (error) {
        if (mine !== requestId) return;
        $('gradeEmpty').hidden = false;
        showStatus(error instanceof CheckError ? error.message : 'Something went wrong. Please try again.');
    } finally {
        if (mine === requestId) setPending(false);
    }
}

/** After "Fix it for me" lands, run the fixed prompt through the same check. */
function watchFixedResult() {
    const result = $('heroResult');
    const resultText = $('heroResultText');
    const out = $('fixedScore');
    if (!result || !resultText || !out) return;

    let timer = null;
    new MutationObserver(() => {
        clearTimeout(timer);
        timer = setTimeout(async () => {
            const text = resultText.textContent.trim();
            if (result.hidden || text.length < MIN_CHARS || text.length > MAX_CHARS || text === lastFixedChecked) return;
            lastFixedChecked = text;
            try {
                const check = await checkPrompt(text);
                const missing = check.followUp ? [] : check.missing.map((key) => ITEMS[key].label);
                out.querySelector('[data-fixed-missing]').textContent = missing.length ? missing.join(', ') : 'none';
                out.hidden = false;
            } catch {
                out.hidden = true;
            }
        }, 400);
    }).observe(resultText, { childList: true, subtree: true, characterData: true });
}

document.addEventListener('DOMContentLoaded', () => {
    const btn = $('gradeBtn');
    const input = $('heroPromptInput');
    if (!btn || !input) return;

    const sync = () => { btn.disabled = !input.value.trim() || !$('gradePending').hidden; };
    sync();
    input.addEventListener('input', sync);
    btn.addEventListener('click', grade);
    input.addEventListener('keydown', (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            // landing.js binds the same shortcut to "Fix it"; here it grades.
            e.stopImmediatePropagation();
            e.preventDefault();
            if (!btn.disabled) grade();
        }
    }, { capture: true });

    watchFixedResult();
});
